<template>
  <div class="lean-form">
    <div>
      <v-card class="mx-auto my-4" max-width="1000" :rounded="false" outlined>
        <v-toolbar v-if="showToolbar" color="blue-grey darken-1" dark flat>
          <v-btn icon :to="'/data/' + datatype">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <lean-form-button-delete v-if="existingSlug" :model="datatype" :slug="existingSlug" />
          <lean-recursive-publish
            v-if="existingSlug && resolved"
            :existing-slug="existingSlug"
            :datatype="datatype"
            :resolved-data="resolved"
            class="px-3"
          />
          <LeanPublish
            v-if="!!currentVersionId && lastPublishVersionId != currentVersionId"
            :current-version-id="currentVersionId"
            :datatype="datatype"
            :language="language"
            :last-publish-version-id="lastPublishVersionId"
            :slug="existingSlug"
          />
        </v-toolbar>
        <div v-if="loading" class="text-center">
          <v-progress-circular class="my-10" indeterminate color="primary"></v-progress-circular>
        </div>
        <v-container v-else fluid class="px-10 py-5">
          <v-form v-if="!!values" ref="form" v-model="valid" lazy-validation>
            <v-row v-if="!existingSlug && !saved" dense class="px-8">
              <v-col cols="12">
                <lean-string
                  name="slug"
                  :label="$t('slug')"
                  :config="{ validations: [{ type: 'regex', match: '^([a-z0-9\\.\\-_])+$' }] }"
                  :initial-value="''"
                  type="string"
                  @change="setSlug($event)"
                />
              </v-col>
            </v-row>
            <lean-form-block
              :initialValues="values"
              :resolved="resolved"
              :schema="schema"
              v-on:change="modifyRecord($event)"
              class="px-8"
            />
          </v-form>
        </v-container>
      </v-card>
      <v-btn
        v-if="isModified"
        class="lean-form-btn-save"
        elevation="2"
        color="blue-grey accent-4"
        dark
        fab
        fixed
        x-large
        bottom
        right
        @click="saveRecord"
      >
        <v-icon>mdi-content-save</v-icon>
      </v-btn>
    </div>
    <v-dialog v-model="showProgressDialog" persistent width="300">
      <v-card>
        <v-toolbar color="blue-grey darken-1" dark flat>
          <v-toolbar-title>{{ $t('upload.headline') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text v-for="(item, index) in uploadProgress" :key="index + item.name" class="pa-5">
          <div class="pb-5">
            {{ item.name }}:
            <v-progress-linear
              stream
              background-color="blue-grey lighten-3"
              color="blue-grey"
              :value="item.progress"
              buffer-value="0"
              height="10"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import LeanFormBlock from '@/components/LeanFormBlock.vue';
import LeanString from '@/components/fields/LeanString';
import { mapGetters, mapMutations, mapState } from 'vuex';
import LeanPublish from '@/components/LeanPublish';
import LeanFormButtonDelete from '@/components/LeanFormButtonDelete';
import LeanRecursivePublish from '@/components/LeanRecursivePublish';

export default {
  name: 'LeanForm',
  components: { LeanRecursivePublish, LeanFormButtonDelete, LeanPublish, LeanFormBlock, LeanString },
  data() {
    return {
      newAttachments: {},
      isModified: false,
      valid: false,
      values: JSON.parse(JSON.stringify(this.record)),
      slug: this.existingSlug,
      saved: false,
      uploadProgress: []
    };
  },
  props: {
    datatype: {
      type: String,
      required: true
    },
    existingSlug: {
      type: String
    },
    record: {
      type: Object
    },
    dbData: {
      type: Object
    },
    resolved: {
      type: Object
    },
    loading: {
      type: Boolean,
      required: true
    },
    showToolbar: {
      type: Boolean,
      default: () => true
    },
    inModal: {
      type: Boolean,
      default: () => false
    }
  },
  computed: {
    ...mapGetters(['workspaceBucket']),
    ...mapState({
      models: 'models',
      language: 'selectedLanguage'
    }),
    showProgressDialog() {
      return this.uploadProgress.length > 0;
    },
    schema() {
      return this.models[this.datatype].schema;
    },
    lastPublishVersionId() {
      return (this.dbData?.destinationmetadata?.tags ?? []).find((tag) => tag.Key === 'srcVersionId')?.Value;
    },
    currentVersionId() {
      return this.dbData?.version;
    }
  },
  watch: {
    record() {
      this.values = JSON.parse(JSON.stringify(this.record));
    }
  },
  methods: {
    ...mapMutations({
      setSnackbar: 'setSnackbar'
    }),
    modifyRecord(data) {
      // TODO: somehow this method is being called with data containing *only* the values
      if (!data.fieldName) {
        return;
      }
      this.isModified = true;
      Object.keys(data.values).map((fieldName) => {
        this.values[fieldName] = data.values[fieldName];
      });
      this.newAttachments = data.newAttachments;
    },
    async validate() {
      await this.$refs.form.validate();
    },
    setSlug(val) {
      // TODO: proper formatting or format on save?
      this.slug = val;
    },
    async saveRecord() {
      await this.validate();
      if (this.valid) {
        const recordKey = `${this.datatype}/${this.slug}/${this.language}/index.json`;
        try {
          // if a slug is provided, then we are in edit mode, overwrite should be possible
          let newSlugCollides = !this.existingSlug;
          try {
            // TODO: change to head() - not sure if the error is the same
            await this.$store.state.sdk.head(this.workspaceBucket, 'data/' + recordKey);
          } catch (err) {
            if (err.name === 'NotFound') {
              // AWS failed to get the object, so it doesn't exist
              newSlugCollides = false;
            } else {
              // oops, other error got thrown... re-throw it, to show in snackbar
              throw err;
            }
          }
          if (!newSlugCollides) {
            // build an object containing the pathnames of the variations
            this.updateValuesWithNewAttachments();
            // upload our index.json document
            await this.$store.state.sdk
              .putObjectToDataBucket(recordKey, JSON.stringify(this.values))
              .catch((reason) => {
                console.error(reason);
                throw new Error('Unable to save record');
              });
            // upload any new attachments
            await this.uploadNewAttachments();
            // reset progress UI, hangs in some cases
            this.uploadProgress = [];
            this.setSnackbar({ message: 'Record saved.', type: 'success' });
            this.isModified = false;
            this.saved = true;
            if (this.inModal) {
              this.$emit('closeModal');
            }
            this.$emit('saved', this.slug);
          } else {
            this.setSnackbar({ message: this.$t('error.slugCollision'), type: 'error' });
          }
        } catch (err) {
          console.error(err);
          this.setSnackbar({ message: err, type: 'error' });
        }
      } else {
        this.setSnackbar({ message: 'Invalid fields, please correct', type: 'error' });
      }
    },
    updateValuesWithNewAttachments() {
      // TODO: the building of the "stored data" is not the responsibility of LeanForm.
      // This should happen in LeanImageField, LeanVideoField, etc –  but in the Field we do not
      // have the datatype and Slug ...
      Object.values(this.newAttachments).forEach((attachment) => {
        const defaultPath = `data/${this.datatype}/${this.slug}/${attachment.path}index.json`;
        // beware the nested variations object
        if (attachment.field.type === 'video' || attachment.field.type === 'audio') {
          attachment.value.variations._default = defaultPath;
        } else if (attachment.field.type === 'image') {
          attachment.value._default = defaultPath;
          // TODO: find the proper place to inject this value
          attachment.field.variations._thumbnail = {
            type: 'jpg',
            width: 200
          };
        } else {
          attachment.value = defaultPath;
        }
        Object.keys(attachment.field.variations || []).forEach((variation) => {
          const variationPath = `data/${this.datatype}/${this.slug}/${attachment.path}${variation}/index.json`;
          // beware the nested variations object
          if (attachment.field.type === 'video' || attachment.field.type === 'audio') {
            attachment.value.variations[variation] = variationPath;
          } else {
            attachment.value[variation] = variationPath;
          }
        });
        this.values[attachment.field.name] = attachment.value;
      });
    },
    uploadNewAttachments() {
      const audioAndVideos = Object.values(this.newAttachments)
        .filter((attachment) => attachment.field.type === 'video' || attachment.field.type === 'audio')
        .map((attachment) => ({
          bucket: this.workspaceBucket,
          // beware the nested variations object
          path: this.values[attachment.field.name].variations._default.replace('index.json', attachment.file.name),
          file: attachment.file
        }));

      const images = Object.values(this.newAttachments)
        .filter((attachment) => attachment.field.type === 'image')
        .map((attachment) => ({
          bucket: this.workspaceBucket,
          path: this.values[attachment.field.name]._default.replace('index.json', attachment.file.name),
          file: attachment.file
        }));

      const attachments = Object.values(this.newAttachments)
        .filter((attachment) => attachment.field.type === 'attachment')
        .map((attachment) => ({
          bucket: this.workspaceBucket,
          path: this.values[attachment.field.name].replace('index.json', attachment.file.name),
          file: attachment.file
        }));

      const all = [...audioAndVideos, ...images, ...attachments];

      all.forEach((attachment) => {
        this.uploadProgress.push({ name: attachment.file.name, progress: 0 });
      });

      return Promise.all(
        all
          .map((data, index) => ({
            ...data,
            progressListener: ({ loaded, total }) => {
              this.uploadProgress.splice(index, 1, {
                name: data.file.name,
                progress: Math.ceil((loaded / total) * 100)
              });
            }
          }))
          .map((data) => this.$store.state.sdk.putFileToBucket(data))
      );
    }
  }
};
</script>
